/**
 * **The per-viewer mask for an {@link AttackForecast}** (`target-hover-forecast` 1.4).
 *
 * It adds no disclosure rule of its own. It reads the two existing ones:
 * - **What a viewer knows about a combatant** is {@link projectCombatantFor}'s answer. When that
 *   returns a masked tier (class or loadout: no stat block), the target's own numbers are unknown:
 *   the defence value, Armor, per-type Resistance and Immunity, Overshield, and the hit chance
 *   derived from the defence. GM, a full-view slot (owner or view grant) and allies get the
 *   target back unmasked, so they get the forecast unchanged. A co-controller is not a full-view
 *   slot (`decisions/0179`), so it still sees the mask.
 * - **Which source names a class-tier carrier hides** is {@link undisclosedSourceNames}, applied
 *   through the same {@link redactContributions} walk the resolution record uses: the name becomes
 *   the sentinel and the value stays, so the itemization still balances the net.
 *
 * `resolutionRedaction.test.ts`'s pairing test pins this module to `projectCombatantFor`.
 */

import type { ActorRole } from "../permissions";
import type { AttackForecast, MaskedAttackForecast } from "./attackForecast";
import { FORECAST_UNKNOWN } from "./attackForecast";
import { projectCombatantFor } from "./projection";
import { redactContributions, undisclosedSourceNames, type UndisclosedSourceNames } from "./resolutionRedaction";
import type { CombatantId, EncounterState } from "./types";

/** Named arguments for {@link maskAttackForecast}: the viewer, as {@link projectCombatantFor} takes it. */
export type MaskAttackForecastArgs = {
  state: EncounterState;
  /** The viewer's slot id, or `null` for a viewer with no owning slot. */
  viewerSlot: string | null;
  role: ActorRole;
};

/**
 * The disclosure tier `projectCombatantFor` masks this combatant to for the viewer, or `undefined`
 * when the viewer sees its stat block. A masked projection is the one without `current` (see
 * `MaskedCombatant`'s doc). An id with no combatant reads as `class`, so a stale forecast fails closed.
 */
function maskedTierOf(id: CombatantId, args: MaskAttackForecastArgs): "class" | "loadout" | undefined {
  if (args.role === "gm") return undefined;
  const combatant = args.state.combatants[id];
  if (combatant === undefined) return "class";
  const projected = projectCombatantFor({ combatant, viewerSlot: args.viewerSlot, role: args.role, state: args.state });
  return "current" in projected ? undefined : projected.disclosure;
}

/** Mask `forecast` for one viewer. Pure; returns a new object and never mutates the input. */
export function maskAttackForecast(forecast: AttackForecast, args: MaskAttackForecastArgs): MaskedAttackForecast {
  const setsFor = (id: CombatantId): UndisclosedSourceNames | undefined => {
    const combatant = args.state.combatants[id];
    return combatant !== undefined && maskedTierOf(id, args) === "class" ? undisclosedSourceNames(combatant) : undefined;
  };
  const contributions = redactContributions(forecast.contributions, setsFor) ?? forecast.contributions;

  if (maskedTierOf(forecast.targetId, args) === undefined) return { ...forecast, contributions };

  // `cappedFrom` goes entirely: even its presence would say the target's Evasion exceeds the cap.
  const { cappedFrom: _cappedFrom, ...defence } = forecast.defence;
  return {
    ...forecast,
    contributions,
    defence: { ...defence, value: FORECAST_UNKNOWN },
    damage:
      forecast.damage === null
        ? null
        : { ...forecast.damage, armor: FORECAST_UNKNOWN, perType: FORECAST_UNKNOWN, overshield: FORECAST_UNKNOWN },
    hitChance: FORECAST_UNKNOWN,
  };
}
